import { type RefObject, useCallback, useEffect, useLayoutEffect, useRef } from 'react';

export type ValidityChangeCallback = (state: { isValid: boolean; reason: keyof ValidityState | null }) => void;

type UseCustomValidityStateParams<T = unknown> = {
  onValidityChange?: ValidityChangeCallback;
  getState: () => Partial<ValidityState> | undefined;
  validate?: (value: T) => string | null;
};

export const useValidityState = <T>({
  onValidityChange,
  getState,
  validate,
}: UseCustomValidityStateParams<T>): void => {
  const previousReason = useRef<keyof ValidityState | null>(null);

  useEffect(() => {
    const validityState = getState();
    if (!validityState) return;

    const validityStateKeys = Object.keys(validityState) as Array<keyof ValidityState>;
    const reason = validityStateKeys.find(key => validityState[key]) ?? null;
    const isValid = reason === null;

    const validityHasChanged = reason !== previousReason.current;

    /*
     * should trigger onValidityChange only if reason has been changed or custom validation has been passed
     * and we need to know if the input is valid or not after the change on the UI
     */
    if (validityHasChanged || validate) {
      onValidityChange?.({ isValid, reason });
    }
    previousReason.current = reason;
  }, [getState, onValidityChange, validate]);
};

/* Long enough that the bubble no longer follows each keystroke, short enough to still feel like a reaction. */
const REPORT_VALIDITY_DELAY_MS = 400;

export type AnyInputElementWithValidityState = {
  checkValidity: () => boolean;
  reportValidity: () => boolean;
  setCustomValidity: (error: string) => void;
  validity: ValidityState;
};

type UseValidityParams<T = unknown> = {
  value: T;
  required: boolean | undefined;
  elementRef: RefObject<AnyInputElementWithValidityState>;
  onValidityChange: ValidityChangeCallback | undefined;
  validate?: (value: T) => string | null;
};

export const useElementValidityState = <T>(
  states: Array<keyof ValidityState>,
  { value, required, elementRef, onValidityChange, validate }: UseValidityParams<T>,
): void => {
  useLayoutEffect(() => {
    if (!elementRef.current) return;

    const customError = validate?.(value as T);
    elementRef.current.setCustomValidity(customError || '');
  }, [value, validate, elementRef, required]);

  /*
   * Reporting on every value change re-opens the native bubble at each keystroke, so it flashes while typing.
   * `validate` is deliberately out of the dependencies: callers pass it inline, so it changes on every render and
   * would restart the timer forever. The validity itself is read when the timer fires, so it is always up to date.
   */
  useEffect(() => {
    const timeout = setTimeout(() => {
      const element = elementRef.current;
      if (!element) return;

      /* reportValidity() pulls the focus back to the element: only report on the input the user is still in. */
      if (element instanceof Element && document.activeElement !== element) return;

      element.reportValidity();
    }, REPORT_VALIDITY_DELAY_MS);

    return () => clearTimeout(timeout);
  }, [value, required, elementRef]);

  const getState = useCallback(() => {
    const validityState = elementRef.current?.validity;
    if (!validityState) return;

    const entries = states.map(state => [state, validityState[state]]);
    return Object.fromEntries(entries);
    // value in dependcies array is kind of hack to update gesState reference
    // and so trigger the useEffect in useValidityState each time value change (it's what we want)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementRef, states, value]);

  useValidityState({ onValidityChange, getState, validate });
};
